import { Deserializable } from '../../protocols/deserializable';
import { Address } from './address';
import { MemberPersonalInformation } from './member-personal-information';
import { ContactInfo } from './contact-info';
import { MemberRecordComment } from '../../../views/records/components/members/ip-member-record/member-record-comments/member-record-comment';
import { MostRecentInvite } from './most-recent-invite';
import { MemberRelationshipStatus } from './member-relationship-status';
import { Spouse } from './spouse';
import { BankAccount } from '../../../views/banking/models/bank-account';
import { Fiduciary } from '../../../views/fiduciaries/models/fiduciary';
import { EmploymentHistory } from './employment-history';
import { MemberAccount } from '../../transactions/member-account';
import { MemberStatusType } from './member-status-type';
import { MemberStatusTypeEnum } from '../enum/member-status-type-enum';
import { IdentityType } from './identity-type';
import { IdentityTypeIdEnum } from '../../enum/shared/identity-type-id.enum';
import { StatusPill } from '../../shared/status-pill';
import { MemberStatusUtils } from '../../../utils/member-status-utils';

export class MemberRecord implements Deserializable {
  public id!: number;
  public firstName!: string;
  public middleName!: string;
  public lastName!: string;
  public preferredName!: string;
  public accounts: MemberAccount[] = [];
  public email!: string;
  public lastLoginDate!: string | null;
  public statusId!: MemberStatusTypeEnum;
  public identityTypeId!: IdentityTypeIdEnum;
  public portalAccessRestricted: boolean = false;
  public portalAccessRestrictedDate: string | null = null;
  public portalAccessRestrictedByName: string | null = null;
  // Everything below is expandable
  public status!: MemberStatusType;
  public identityType!: IdentityType;
  public address!: Address;
  public personalInformation!: MemberPersonalInformation;
  public phoneNumber!: ContactInfo;
  public activeRelationshipStatus!: MemberRelationshipStatus;
  public comments!: MemberRecordComment[];
  public mostRecentInvite!: MostRecentInvite;
  public pastRelationshipStatuses!: MemberRelationshipStatus[];
  public spouses!: Spouse[];
  public bankAccounts!: BankAccount[];
  public activeLegalFiduciary!: Fiduciary;
  public pastLegalFiduciaries!: Fiduciary[];
  public employmentHistory!: EmploymentHistory[];

  // Not From API
  public fullName!: string;
  public statusPill!: StatusPill;
  public isNonMember: boolean = false;

  // TODO: This is a fake property until backend is updated
  public boardDirectedRetirement: boolean = false;

  onDeserialize(): void {
    const Deserialize = window?.injector?.Deserialize;
    this.fullName = `${this?.firstName} ${this?.lastName}`.trim();
    this.statusPill = MemberStatusUtils.statusPill(this.statusId);
    this.isNonMember = this.identityTypeId === IdentityTypeIdEnum.NonMember;
    if (!!this.status) {
      this.status = Deserialize?.instanceOf(MemberStatusType, this.status);
    }
    if (!!this.identityType) {
      this.identityType = Deserialize?.instanceOf(IdentityType, this.identityType);
    }
    if (!!this.address) {
      this.address = Deserialize?.instanceOf(Address, this.address);
    }
    if (!!this.personalInformation) {
      this.personalInformation = Deserialize?.instanceOf(MemberPersonalInformation, this.personalInformation);
    }
    if (!!this.phoneNumber) {
      this.phoneNumber = Deserialize?.instanceOf(ContactInfo, this.phoneNumber);
    }
    if (!!this.comments) {
      this.comments = Deserialize?.arrayOf(MemberRecordComment, this.comments);
    }
    if (!!this.mostRecentInvite) {
      this.mostRecentInvite = window?.injector?.Deserialize?.instanceOf(MostRecentInvite, this.mostRecentInvite);
    }
    if (!!this.activeRelationshipStatus) {
      this.activeRelationshipStatus = Deserialize?.instanceOf(MemberRelationshipStatus, this.activeRelationshipStatus);
    }
    if (!!this.pastRelationshipStatuses) {
      this.pastRelationshipStatuses = Deserialize?.arrayOf(MemberRelationshipStatus, this.pastRelationshipStatuses);
      this.pastRelationshipStatuses.sort((a, b) => (b.effectiveDate ?? '').localeCompare(a.effectiveDate ?? ''));
    }
    if (!!this.spouses) {
      this.spouses = Deserialize?.arrayOf(Spouse, this.spouses);
    }
    if (!!this.bankAccounts) {
      this.bankAccounts = Deserialize?.arrayOf(BankAccount, this.bankAccounts);
    }
    if (!!this.activeLegalFiduciary) {
      this.activeLegalFiduciary = Deserialize?.instanceOf(Fiduciary, this.activeLegalFiduciary);
    }
    if (this.pastLegalFiduciaries?.length) {
      this.pastLegalFiduciaries = Deserialize?.arrayOf(Fiduciary, this.pastLegalFiduciaries);
    }
    if (this.employmentHistory?.length) {
      this.employmentHistory = Deserialize?.arrayOf(EmploymentHistory, this.employmentHistory);
      this.employmentHistory.sort((a, b) => a.employer.localeCompare(b.employer));
    }
    if (this.accounts) {
      this.accounts = Deserialize?.arrayOf(MemberAccount, this.accounts);
    }
  }
}
